// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY_PREFIX = 'knoll:canvas:hidden'

/**
 * Manages the set of hidden item IDs for a session.
 * Syncs with localStorage to persist the "Cleared" state.
 */
export function useHiddenItems(sessionId: string | null) {
    const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())

    // Load hidden items from localStorage on session change
    useEffect(() => {
        if (!sessionId) {
            setHiddenIds(new Set())
            return
        }

        const key = `${STORAGE_KEY_PREFIX}:${sessionId}`
        try {
            const stored = localStorage.getItem(key)
            if (stored) {
                const parsed = JSON.parse(stored)
                if (Array.isArray(parsed)) {
                    setHiddenIds(new Set(parsed))
                }
            } else {
                setHiddenIds(new Set())
            }
        } catch (error) {
            console.warn('Failed to load hidden items from localStorage:', error)
            setHiddenIds(new Set())
        }
    }, [sessionId])

    const hideAll = useCallback((ids: string[]) => {
        if (!sessionId) return
        const next = new Set(ids)
        setHiddenIds(next)
        const key = `${STORAGE_KEY_PREFIX}:${sessionId}`
        try {
            localStorage.setItem(key, JSON.stringify([...next]))
        } catch (error) {
            console.warn('Failed to save hidden items to localStorage:', error)
        }
    }, [sessionId])

    const showAll = useCallback(() => {
        if (!sessionId) return
        setHiddenIds(new Set())
        const key = `${STORAGE_KEY_PREFIX}:${sessionId}`
        try {
            localStorage.removeItem(key)
        } catch (error) {
            console.warn('Failed to clear hidden items from localStorage:', error)
        }
    }, [sessionId])

    return {
        hiddenIds,
        hideAll,
        showAll,
    }
}
